// iter159/161 — Auto-login landing for Toolkit links. The exchange is started by an
// inline script in public/index.html (window.__mdmSso) before the bundle loads.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { LinkIcon, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, purgeStaleSession } from "@/lib/AuthContext";
import { SsoConfirm, SsoNoPassword } from "@/components/SsoConfirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SSO_ARRIVAL_KEY = "mdm.ssoArrival";

const removeBootLoader = () => document.getElementById("sso-boot")?.remove();

function startExchange() {
  if (window.__mdmSso) { const p = window.__mdmSso; window.__mdmSso = null; return p; }
  const token = new URLSearchParams(window.location.search).get("token") || "";
  window.history.replaceState(null, "", window.location.pathname);
  if (!token) return Promise.resolve({ status: 400, data: {} });
  return axios.post(`${API}/sso/toolkit/exchange`, { token })
    .then((r) => ({ status: r.status, data: r.data }))
    .catch((e) => ({ status: e?.response?.status || 0, data: e?.response?.data || {} }));
}

export default function SsoToolkit() {
  const { adoptSession } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("working");
  const [confirm, setConfirm] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const pending = startExchange();
    purgeStaleSession();
    pending.then(({ status: code, data }) => {
      if (data?.needsConfirm) { setConfirm(data); setStatus("confirm"); removeBootLoader(); return; }
      if (code !== 200 || !data?.ok || !data?.token) { setStatus(code === 429 ? "rate" : "error"); removeBootLoader(); return; }
      try { sessionStorage.setItem(SSO_ARRIVAL_KEY, "1"); } catch { /* ignore */ }
      adoptSession(data.token, data.user);
      navigate(data.redirect === "/dashboard" ? "/dashboard" : "/choose-plan", { replace: true });
      requestAnimationFrame(removeBootLoader);
    });
  }, [adoptSession, navigate]);

  if (status === "working") return <main className="min-h-screen bg-sand-50" data-testid="sso-working" />;

  if (status === "confirm") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sand-50 px-6" data-testid="sso-confirm">
        {confirm.noPassword ? <SsoNoPassword phoneLast4={confirm.phoneLast4} /> : <SsoConfirm ticket={confirm.ticket} phoneLast4={confirm.phoneLast4} />}
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-sand-50 px-6" data-testid="sso-expired">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-white p-6 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <LinkIcon className="h-6 w-6" />
        </div>
        <h1 className="font-heading text-lg font-bold text-ink-900" data-testid="sso-expired-title">
          Link expired, please tap again in Teacher Utility
        </h1>
        {status === "rate" && (
          <p className="mt-2 text-xs text-ink-700" data-testid="sso-rate-limited">Too many attempts. Please wait a minute.</p>
        )}
        <Button asChild className="mt-5 w-full" data-testid="sso-login-btn">
          <Link to="/login"><LogIn className="mr-2 h-4 w-4" /> Log in normally</Link>
        </Button>
      </div>
    </main>
  );
}
